import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  
  Typography,
  useMediaQuery,
  useTheme ,Theme ,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@mui/icons-material/Close';
import { Field, Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import schemas from '../../../data/schemas';
import { currencyHelper } from '../../../helpers/currencyHelper';
import { linkHelper } from '../../../helpers/linkHelper';
import { numberHelper } from '../../../helpers/numberHelper';
import { createLink, shareLink } from '../../../store/action_creators/links.actions';
import { AppActions } from '../../../store/config/ActionTypes';
import { InclusionLawEnum, LinkTypeEnum, Status } from '../../../store/config/enums';
import {
  CreateLinkRequest,
  CreateSplitLinkRequest,
  Link,
  RootState,
  ShareLinkRequest,
} from '../../../store/config/types';
import { CustomSnackbar } from '../../CustomSnackbar';
import { ShareByEmail } from '../../Icons/ShareByEmail';
import { ShareByQr } from '../../Icons/ShareByQr';
import { ShareByWhatsapp } from '../../Icons/ShareByWhatsapp';
import { FormTextField } from '../../forms/FormTextField';
import { QrCodeDialog } from '../QRCodeDialog';
import { ShareByWhatsAppDialog } from '../ShareByWhatsAppDialog';
import { ShareOptions } from '../ShareOptions';
import colors from './../../../styles/_variables.module.scss';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    valueFieldsContainer: {
      display: 'flex',
    },
    currencySelect: {
      width: '40%',
      marginRight: '1rem',
    },
    valueField: {
      fontSize: '1.5rem',
    },
    title: {
      display: 'flex',
      alignItems: 'baseline',
      '& svg': {
        color: theme.palette.secondary.main,
        marginRight: '0.5rem',
      },
    },
    linkContainer: {
      border: `solid 1px ${colors.veryLightGrey}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '1rem',
      marginTop: '2rem',
      '& button': {
        minWidth: '5.9375rem',
        marginLeft: '2rem',
        [theme.breakpoints.down('sm')]: {
          marginLeft: 0,
          width: '100%',
        },
      },
      [theme.breakpoints.down('sm')]: {
        padding: 0,
        border: 'none',
      },
    },
    linkInfo: {
      margin: '1rem 0',
    },
    shareBoxesContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '1rem 0',
      gap: '1rem',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
      '& button': {
        width: '100%',
        color: colors.white,
        padding: '0.6rem 1rem',
        '& .MuiButton-label': {
          display: 'flex',
          gap: 8,
        },
      },
    },
    whatsapp: {
      backgroundColor: colors.limeGreen,
      '&:hover': {
        backgroundColor: colors.darkLimeGreen,
      },
    },
    email: {
      backgroundColor: colors.purple,
      '&:hover': {
        backgroundColor: colors.darkPurple,
      },
    },
    qr: {
      backgroundColor: colors.strongCyan,
      '&:hover': {
        backgroundColor: colors.darkStrongCyan,
      },
    },
    shareBox: {
      border: `solid 1px ${theme.palette.background.default}`,
      borderRadius: theme.shape.borderRadius,
      padding: '2rem',
      height: '170px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      '& svg': {
        marginBottom: '1rem',
      },
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        height: 'auto',
        padding: '1rem',
      },
      '&:not(:first-child)': {
        marginLeft: '1rem',
        [theme.breakpoints.down('sm')]: {
          marginLeft: 0,
          marginTop: '1rem',
        },
      },
    },
    formRow: {
      display: 'flex',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
      '& button': {
        [theme.breakpoints.up('sm')]: {
          marginLeft: '1rem',
        },
        [theme.breakpoints.down('sm')]: {
          margin: '1rem 0',
        },
      },
    },
    linkUrl: {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      fontSize: '0.9rem',
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    subtitle: {
      marginTop: '1rem',
      [theme.breakpoints.down('sm')]: {
        marginTop: 0,
      },
    },
    divider: {
      margin: '1rem 0',
    },
    shareTitle: {
      textAlign: 'center',
      color: theme.palette.text.disabled,
    },
    qrIcon: {
      color: colors.white,
      padding: '2px',
      borderRadius: '2px',
      marginBottom: '0px!important',
    },
    qrIconContainer: {
      width: '64px',
      height: '64px',
      borderRadius: '50%',
      backgroundColor: colors.pureBlue,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '1rem',
    },
  }),
);

export interface ShareLinkDialogProps {
  open: boolean;
  setOpen(value: boolean): void;
  link: Link;
}

const mapStateToProps = (state: RootState) => ({
  auth: state.auth,
  links: state.links,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, any, AppActions>) => ({
  shareLink: (shareRequest: ShareLinkRequest) => dispatch(shareLink(shareRequest)),
  createLink: (createRequest: CreateLinkRequest) => dispatch(createLink(createRequest)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsType = PropsFromRedux & ShareLinkDialogProps;

function ShareLinkDialog({ auth, links, shareLink, createLink, open, setOpen, link }: PropsType) {
  const classes = useStyles(useTheme());
  const [openEmailOptions, setOpenEmailOptions] = useState<boolean>(false);
  const [openWhatsAppOptions, setOpenWhatsAppOptions] = useState<boolean>(false);
  const [openQrDialog, setOpenQrDialog] = useState<boolean>(false);
  const [copyingLink, setCopyingLink] = useState<boolean>(false);
  const [sharingLink, setSharingLink] = useState<boolean>(false);
  const [creatingOneUseLink, setCreatingOneUseLink] = useState<boolean>(false);
  const [newLink, setNewLink] = useState<Link | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const closeDialog = () => {
    setOpen(false);
    setNewLink(null);
  };

  const copyToClipboard = () => {
    setCopyingLink(true);
    navigator.clipboard.writeText(link.url ? link.url : '');
  };

  const shareEmail = (email: string) => {
    shareLink({ linkId: link.id, email });
  };

  const createOneUseLink = (values: { reference: string }) => {
    if (link.installmentsDto) {
      setCreatingOneUseLink(true);
      const baseRequest = {
        businessId: auth.account?.business.id !== undefined ? auth.account?.business.id! : 0,
        amount: link.amount,
        description: link.description ?? '',
        reference: values.reference,
        userId: auth.account ? auth.account.userId : '',
        linkType: LinkTypeEnum.ONETIME,
        validUntil: null,
        vatRate: link.vatRate,
        installmentsIds: link.installmentsDto.map((installment) => installment.id),
        paymentType: link.paymentType,
              inclusionLaw: link.inclusionLaw ?? InclusionLawEnum.NONE,
      };

      if (link.splitLinkDto) {
        const splitLinkRequest: CreateSplitLinkRequest = {
          ...baseRequest,
          splitLinkRequest: {
            subBusinessId: link.splitLinkDto.subBusinessId,
            amount: link.splitLinkDto.amount.value,
            vatRate: link.splitLinkDto.vatRate,
          },
        };

        createLink(splitLinkRequest);
      } else {
        createLink(baseRequest);
      }
    }
  };

  const closeSnack = () => {
    if (sharingLink) {
      setSharingLink(false);
    } else if (creatingOneUseLink) {
      setCreatingOneUseLink(false);
    } else if (copyingLink) {
      setCopyingLink(false);
    }
  };

  useEffect(() => {
    if (creatingOneUseLink) {
      setNewLink(links.latestLink);
    }
  }, [links.latestLink, creatingOneUseLink]);

  return (
    <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm" className="dialog">
      <DialogTitle classes={{ root: 'dialog-title' }}>
        <div />
        <CloseIcon fontSize="medium" className="icon" onClick={closeDialog} />
      </DialogTitle>
      <DialogContent classes={{ root: 'dialog-content' }}>
        <div className={classes.title}>
          <Typography variant="h4">{`Compartir link ${
            newLink || link.linkType === LinkTypeEnum.ONETIME
              ? 'de 1 uso'
              : link.linkType === LinkTypeEnum.PERMANENT
              ? 'multiuso'
              : ''
          }`}</Typography>
        </div>
        <div className={classes.linkContainer}>
          <Typography className={classes.linkUrl}>{newLink ? newLink.url : link?.url}</Typography>
          <Button variant="contained" color="primary" onClick={copyToClipboard} disableElevation>
            {`Copiar${isMobile ? ' link al portapapeles' : ''}`}
          </Button>
        </div>
        <Typography
          variant="h5"
          component="p"
          className={classes.linkInfo}
        >{`${currencyHelper.getCurrencyToken(
          newLink ? newLink.amount.currency : link?.amount.currency,
        )} ${numberHelper.getNumberWithDots(
          newLink ? newLink.amount.value : linkHelper.getTotalLinkAmount(link),
        )} ${newLink ? newLink.description : link && link.description ? link.description : ''}`}</Typography>

        {newLink === null && link.linkType === LinkTypeEnum.PERMANENT && (
          <Formik
            initialValues={{
              reference: link?.reference ? link.reference : '',
            }}
            onSubmit={createOneUseLink}
            validationSchema={schemas.AddReferenceSchema}
          >
            <Form className="form">
              <div className={classes.formRow}>
                <Field
                  name="reference"
                  component={FormTextField}
                  type="text"
                  placeholder="Referencia o destinatario"
                  margin="none"
                />
                <Button type="submit" variant="outlined" color="secondary" disableElevation>
                  Guardar
                </Button>
              </div>
              <Typography variant="subtitle2" className={classes.subtitle}>
                Al agregar referencia o destinatario, se creará un link de 1 uso a partir de este link
                multiuso.
              </Typography>
            </Form>
          </Formik>
        )}
        <Divider className={classes.divider} />
        <Typography variant="subtitle2" className={classes.shareTitle}>
          Compartir por
        </Typography>
        <div className={classes.shareBoxesContainer}>
          <Button className={classes.whatsapp} onClick={() => setOpenWhatsAppOptions(true)}>
            <ShareByWhatsapp />
            Whatsapp
          </Button>
          <Button className={classes.email} onClick={() => setOpenEmailOptions(true)}>
            <ShareByEmail />
            Email
          </Button>
          {link.url && (
            <Button className={classes.qr} onClick={() => setOpenQrDialog(true)}>
              <ShareByQr />
              Código QR
            </Button>
          )}
        </div>
      </DialogContent>
      <ShareOptions open={openEmailOptions} setOpen={setOpenEmailOptions} share={shareEmail} />
      <ShareByWhatsAppDialog open={openWhatsAppOptions} setOpen={setOpenWhatsAppOptions} url={link.url!} />
      <QrCodeDialog
        open={openQrDialog}
        setOpen={setOpenQrDialog}
        url={link.url!}
        description={link.description!}
        price={link.amount}
      />
      <CustomSnackbar
        open={creatingOneUseLink && (links.createLinkSuccess || links.createLinkErrorMessage !== null)}
        message={
          links.createLinkSuccess
            ? 'Se creó un nuevo link de 1 uso.'
            : 'Ocurrió un error al crear un nuevo link de 1 uso.'
        }
        handleClose={closeSnack}
        type={links.createLinkSuccess ? Status.SUCCESS : Status.ERROR}
      />
      <CustomSnackbar
        open={copyingLink}
        message="Link copiado al portapapeles."
        handleClose={closeSnack}
        type={Status.SUCCESS}
      />
    </Dialog>
  );
}

export default connector(ShareLinkDialog);
